import React, { useMemo, useEffect, useState } from 'react';
import { Button, notification, Card, Row, Col, Menu } from 'antd';
import './Notification.scss';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BellFilled, BellOutlined, InfoCircleFilled, WarningFilled } from '@ant-design/icons';
import { getUserInfo, setUserInfo } from '../../helpers/localStorageHandler';
import { API_ENDPOINTS } from '../../assets/api/endpoints';
import axios from 'axios';
import { notificationContentGenerator } from 'helpers/notificationContent';
import { getNotificationType } from 'helpers/getNotificationType';
import { t } from 'i18next';
import NoNotificationsCard from './components/NoNotificationsCard';
import NotificationCard from './components/NotificationCard';
import useComponentVisible from './useComponentVisible';
import ReactTimeAgo from 'react-time-ago';
import { getUserLanguageInCookie } from 'helpers/cookiesHandler';
import { getLanguage } from 'helpers/getLanguage';
import { useMainContext } from 'store/MainContext';
import { NotificationActionType } from 'helpers/getNotificationsActionTypes';
import { useNotificationTypeContext } from 'store/NotificationTypeContext';
import { useConfigurationContext } from 'store/configurationContext';
import { useAutoApprovalContext } from 'store/AutoApprovalContext';
import { useResilientStompClient } from '../../hooks/useResilientStompClient';
import { fetchSocketJwtToken } from '../../helpers/socketToken';
import { chatDebug } from '../../helpers/chatDebugLog';
import { Client } from 'stompjs';

const Notification: React.FC = () => {
	const location = useLocation();
	const { mainState, setMainState } = useMainContext();
	const { isAutoApproval } = useAutoApprovalContext();
	const { setNotificationType } = useNotificationTypeContext();
	const [api, contextHolder] = notification.useNotification({ maxCount: 3 });
	const { configurationState } = useConfigurationContext();
	const userId = useMemo(() => getUserInfo()?.userId || '', []);
	const [notificationsCount, setNotificationsCount] = useState(0);
	const [notifications, setNotifications] = useState<Record<any, any>[]>([]);
	const [loading, setLoading] = useState(false);
	const isMerchant = useMemo(() => getUserInfo()?.isMerchant ?? false, []);
	const navigate = useNavigate();
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

	const getNotificationStyle = (action: string, isSmallScreen: boolean) => {
		const top = isSmallScreen ? '0px' : '50px';
		switch (action) {
			case 'WARNING':
				return { backgroundColor: '#fbe7c6', top };
			case 'ACTION':
				return { backgroundColor: '#b7f7c4', top };
			default:
				return { backgroundColor: '#b3e6f5', top };
		}
	};

	const resolveRedirectUrl = (messageObject: any): string => {
		switch (messageObject.notification) {
			case 'NEW_ORDER':
			case 'ORDER_NEW_MESSAGE':
			case 'ORDER_EXPIRY_WARNING':
			case 'ORDER_EXPIRED':
			case 'ORDER_STATUS_UPDATE':
			case 'APPEAL_REASSIGNED':
				return `/order-life-cycle?id=${messageObject.properties.orderNumber}`;
			case 'OFFER_DISABLED':
			case 'EXCHANGE_RATE_UPDATE':
				return '/my-offers';
			case 'NEW_OPERATION':
			case 'REQUEST_ACTION':
				return `/wallet?transactionNumber=${messageObject.properties.transactionNumber}&type=${messageObject.properties.type}`;
			default:
				return '/notifications';
		}
	};

	const { stompClient } = useResilientStompClient({
		url: API_ENDPOINTS.notification,
		getSocketJwtToken: fetchSocketJwtToken,
		label: 'notifications',
		onConnect: (client: Client) => {
			const role = isMerchant ? 'MERCHANT' : 'CLIENT';
			const publicTopic = API_ENDPOINTS.getPublicNotificationTopic;
			const roleTopic = API_ENDPOINTS.getRoleNotificationTopic.replace('%role%', role);
			const userTopic = userId ? API_ENDPOINTS.getUserNotificationTopic.replace('%userId%', userId.toString()) : null;
			chatDebug.info('notifications', 'subscribing', { role, hasUserTopic: !!userTopic });
			const subscribeCallback = async (msgReceived: any) => {
				try {
					const messageObject = JSON.parse(msgReceived.body);
					await new Audio('/sounds/Bell-Notification.mp3').play();

					const title = t<string>(messageObject.notification);
					const content = notificationContentGenerator(messageObject.notification, {
						...messageObject.properties,
						createdAt: messageObject.createdAt,
						isAutoApproval,
					});

					const notificationType = getNotificationType(messageObject.notification);

					api.info({
						onClick: () => navigate(resolveRedirectUrl(messageObject)),
						message: title,
						description: content,
						className: `NOTIFICATION_${title}`,
						placement: 'topRight',
						icon:
							notificationType === 'ACTION' ? (
								<BellFilled style={{ color: '#000000' }} />
							) : notificationType === 'WARNING' ? (
								<WarningFilled style={{ color: '#000000' }} />
							) : (
								<InfoCircleFilled style={{ color: '#000000' }} />
							),
						style: getNotificationStyle(notificationType, windowWidth <= 500),
					});

					if (
						[
							NotificationActionType.USER_BLOCK,
							NotificationActionType.USER_SUSPEND,
							NotificationActionType.USER_UNBLOCK,
							NotificationActionType.USER_UNSUSPEND,
						].includes(messageObject.notification)
					) {
						const userStatusRes = await axios.get(API_ENDPOINTS.currentUserStatus);
						const updatedUserInfo: any = { ...getUserInfo(), userStatus: userStatusRes.data.status };
						setUserInfo(updatedUserInfo);
						setMainState({ ...mainState, userStatus: userStatusRes.data.status });
					}

					setNotificationType(messageObject.notification);
					const countRes = await axios.get(API_ENDPOINTS.notificationsCount);
					setNotificationsCount(countRes.data);
				} catch (err) {
					console.error('Notification Handling Error:', err);
				}
			};

			client.subscribe(publicTopic, subscribeCallback);
			client.subscribe(roleTopic, subscribeCallback);
			if (userTopic) client.subscribe(userTopic, subscribeCallback);
		},
		onDisconnect: () => chatDebug.warn('notifications', 'disconnected, will keep retrying'),
	});

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const fetchNotificationsData = async () => {
		setLoading(true);
		try {
			const { data } = await axios.get(`${API_ENDPOINTS.notifications}?size=5`);
			const mapped = data.data.map((n: any) => ({
				...n,
				title: t<string>(n.notification),
				timestamp: <ReactTimeAgo date={n.createdAt} locale={getLanguage(getUserLanguageInCookie())} />,
				content: notificationContentGenerator(n.notification, {
					...n.properties,
					createdAt: n.createdAt,
					isAutoApproval,
				}),
				type: getNotificationType(n.notification),
			}));
			setNotifications(mapped);
		} catch (e) {
			console.error(e);
		} finally {
			setLoading(false);
		}

		try {
			const countRes = await axios.get(API_ENDPOINTS.notificationsCount);
			setNotificationsCount(countRes.data);
		} catch (e) {
			console.error(e);
		}
	};
	useEffect(() => {
		fetchNotificationsData();
	}, []);

	const themeColor = configurationState.find((item) => item.configKey === 'theme_color')?.value || '';
	const secondaryColor = configurationState.find((item) => item.configKey === 'secondary_color')?.value || '';

	return (
		<div
			className={
				location.pathname === '/notifications'
					? 'invisible bill-container flex flex-col'
					: 'bill-container flex flex-col'
			}
			ref={ref as React.RefObject<HTMLDivElement>}
		>
			<Button
				onClick={() => setIsComponentVisible(!isComponentVisible)}
				className='bill-button bg-transparent border-none text-white'
			>
				<BellOutlined style={{ fontSize: '20px', color: secondaryColor }} />
				{notificationsCount && !isComponentVisible ? (
					<span className='bill-icon' style={{ backgroundColor: themeColor, color: secondaryColor }}>
						{notificationsCount > 99 ? '+99' : notificationsCount}
					</span>
				) : (
					<span className='bill-icon-empty'></span>
				)}
			</Button>
			{isComponentVisible && (
				<Menu className='notifications-dropdown'>
					{notifications.length === 0 ? (
						<NoNotificationsCard loading={loading} />
					) : (
						<>
							{notifications.map((el, i) => (
								<React.Fragment key={i}>
									<Link to={resolveRedirectUrl(el)} onClick={() => setIsComponentVisible(false)}>
										<NotificationCard
											type={el.type}
											notification={el.notification}
											properties={el.properties}
											timestamp={el.timestamp}
										/>
									</Link>
									{i === notifications.length - 1 && (
										<Card className='view-more-notifications-card'>
											<Row className='flex justify-end' align='middle'>
												<Col className='notification-content'>
													<Link to='/notifications' onClick={() => setIsComponentVisible(false)}>
														<Button>{t('seeMore')}</Button>
													</Link>
												</Col>
											</Row>
										</Card>
									)}
								</React.Fragment>
							))}
						</>
					)}
				</Menu>
			)}
			{contextHolder}
		</div>
	);
};

export default Notification;
